import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { flattenToAppURL, Helmet } from '@plone/volto/helpers';
import { UniversalLink } from '@plone/volto/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import CTABanner from '../../Molecules/CTABanner/CTABanner';
import TeaserOverlay from '../../Atoms/TeaserOverlay';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import ResponsiveGrid from '../../Atoms/ResponsiveGrid';
import Carousel from '../../Atoms/Carousel';
import ResponsiveComponentSwitch from '../../Atoms/ResponsiveComponentSwitch';
import RichText from '../../Atoms/RichText';
import { ESSButton } from '../../Atoms/ESSButton';
import { validateType } from '../../../helpers/contentValidators';
import { formatNewsDate } from '../../../helpers/multimediaUtils';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';
import {
  ContentWrapper,
  ContentWide,
  ContentNarrow,
  PageSection,
} from 'volto-essvoltocomponents/components/Layout';

import {
  useHomeContent,
  useHomeTeams,
  useHomeHighlightedWork,
  useHomeNews,
  useHomePartners,
} from './useHomeData';

import heroVideo from '../../../../public/hero_video.mp4';
import heroVideoWebm from '../../../../public/hero_video.webm';
import './HomeView.scss';

// ---------------------------------------------------------------------------
// i18n
// ---------------------------------------------------------------------------
const messages = defineMessages({
  heroTitle: {
    id: 'home.hero.title',
    defaultMessage: 'We work for a more resilient and sustainable society',
  },
  heroDescription: {
    id: 'home.hero.description',
    defaultMessage: 'co-producing climate and air quality services',
  },
  learnMore: {
    id: 'home.learnMore',
    defaultMessage: 'LEARN MORE',
  },
  aboutUsTitle: {
    id: 'home.aboutUs.title',
    defaultMessage: 'ABOUT US',
  },
  ourTeamsTitle: {
    id: 'home.ourTeams.title',
    defaultMessage: 'OUR TEAMS',
  },
  ourWorkTitle: {
    id: 'home.ourWork.title',
    defaultMessage: 'OUR WORK',
  },
  newsTitle: {
    id: 'home.news.title',
    defaultMessage: 'NEWS',
  },
  partnersText: {
    id: 'home.partners.text',
    defaultMessage: 'BSC is a public consortium made up of:',
  },
  exploreMore: {
    id: 'home.exploreMore',
    defaultMessage: 'VIEW ALL',
  },
  exploreMoreNews: {
    id: 'home.exploreMoreNews',
    defaultMessage: 'VIEW ALL NEWS',
  },
});

const isDev = process.env.NODE_ENV === 'development';

const TEAMS_GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 600, columns: 2 },
  { minWidth: 1200, columns: 4 },
];

const WORK_GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 640, columns: 2 },
  { minWidth: 1024, columns: 4 },
];

const NEWS_GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 640, columns: 1 },
  { minWidth: 1024, columns: 3 },
];

const EMPTY_ARRAY = [];

// ---------------------------------------------------------------------------
// HomeView Component
// ---------------------------------------------------------------------------
const HomeView = ({ content }) => {
  const intl = useIntl();

  // --- Data hooks ---
  const { homeContent } = useHomeContent(content);
  const summaryAbout =
    homeContent?.summary_about_project?.data || homeContent?.description || '';

  // "highlighted_work" relationlist validation
  const highlightedWork = homeContent?.highlighted_work || EMPTY_ARRAY;
  const validHighlightedWork = highlightedWork.filter((item) =>
    validateType(item, ['project', 'sector', 'service', 'competence']),
  );
  const { items: highlightedWorkItems, loaded: workLoaded } =
    useHomeHighlightedWork(validHighlightedWork);

  const { items: teams, loaded: teamsLoaded } = useHomeTeams();
  const { items: newsItems, loaded: newsLoaded } = useHomeNews();
  const { items: partners, loaded: partnersLoaded } =
    useHomePartners(homeContent);

  // --- Card renderers ---

  // Our Teams: TeaserOverlay
  const teamCards = teams.map((item) => {
    const imageUrl = resolveImageURL(item, '/@@images/thumbnail_image/preview');
    return (
      <UniversalLink
        key={item['@id']}
        href={flattenToAppURL(item['@id'])}
        className="home-view__team-link"
      >
        <TeaserOverlay
          imgSource={imageUrl}
          title={item.title || ''}
          description={item.description || ''}
          aspectRatio={{ xs: '0.9', sm: '0.9', xl: '0.9', xxl: '0.85' }}
          mainColor="var(--color-secondary-02)"
          altColor="var(--color-primary-01)"
          borderColor="var(--color-primary-02)"
          width="250px"
          titleMaxWidth="160px"
        />
      </UniversalLink>
    );
  });

  const workCards = highlightedWork?.map((item) => {
    const imageUrl = resolveImageURL(item, '/@@images/thumbnail_image/preview');
    return (
      <TeaserCardVertical
        key={item['@id']}
        title={item.title || ''}
        description={item?.short_description?.data || ''}
        imageUrl={imageUrl}
        url={item['@id'] || ''}
      />
    );
  });

  const newsCards = newsItems?.map((item) => {
    const imageUrl = resolveImageURL(item, '/@@images/thumbnail_image/preview');
    return (
      <TeaserCardVertical
        key={item['@id']}
        title={item.title || ''}
        description={item?.short_description?.data || ''}
        imageUrl={imageUrl}
        url={item['@id'] || ''}
        caption={formatNewsDate(item.effective || item.EffectiveDate)}
      />
    );
  });

  const partnerCards = partners?.map((item, index) => {
    const imageUrl = resolveImageURL(item, '/@@images/image/preview');
    const logoCard = (
      <TeaserCardVertical
        key={item['@id'] || index}
        imageUrl={imageUrl}
        fitImage={true}
        showCTA={false}
        className="home-view__partner-image"
        border="none"
        bgColor="transparent"
      />
    );
    const linkUrl = item.description || '';
    return (
      <div key={item['@id']} className="home-view__partner-item">
        {linkUrl ? (
          <a href={linkUrl} target="_blank" rel="noopener noreferrer">
            {logoCard}
          </a>
        ) : (
          logoCard
        )}
      </div>
    );
  });

  return (
    <div className="home-view">
      <Helmet title={homeContent?.title || 'Home'} />

      <CTABanner
        title={intl.formatMessage(messages.heroTitle)}
        description={intl.formatMessage(messages.heroDescription)}
        ctaText={intl.formatMessage(messages.learnMore)}
        ctaTo="/about-us/who-we-are"
        ctaVariant="negative"
        variant="dark"
        alignment="center"
        minHeight="600px"
        backgroundVideo={heroVideo}
        backgroundVideoWebm={heroVideoWebm}
        overlayColor="var(--color-primary-01)"
        overlayOpacity={0.6}
        titleColor="var(--color-secondary-02)"
        descriptionColor="var(--color-secondary-02)"
        className="home-view__hero"
      />

      <CTABanner
        title={intl.formatMessage(messages.aboutUsTitle)}
        description={summaryAbout}
        ctaText={intl.formatMessage(messages.learnMore)}
        ctaTo="/about-us/who-we-are"
        ctaVariant="negative"
        backgroundColor="var(--color-primary-02)"
        alignment="center"
        titleColor="var(--color-secondary-02)"
        descriptionColor="var(--color-secondary-02)"
        className="home-view__about-section"
      />

      {/* ================================================================
          SECTION 3: Our Teams (from /about-us folder, TeaserOverlay, grid)
          ================================================================ */}
      <PageSection
        className="home-view__teams-section"
        backgroundColor="var(--color-secondary-02)"
      >
        <ContentWrapper>
          <ContentWide alignment="center" className="home-view__section-header">
            <h1 className="home-view__section-title">
              {intl.formatMessage(messages.ourTeamsTitle)}
            </h1>
            <p>Discover the multidisciplinary teams driving our work.</p>
          </ContentWide>
          <ContentWide
            alignment="center"
            className="home-view__teams-section-content"
          >
            {teamsLoaded && teamCards.length > 0 && (
              <ResponsiveGrid
                breakpoints={TEAMS_GRID_BREAKPOINTS}
                className="home-view__teams-grid"
              >
                {teamCards}
              </ResponsiveGrid>
            )}
            <div className="home-view__cta-link">
              <ESSButton variant="link-icon" href="/about-us/who-we-are">
                {intl.formatMessage(messages.exploreMore)}
                <FontAwesomeIcon icon={faChevronRight} size="sm" />
              </ESSButton>
            </div>
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* ================================================================
          SECTION 4: Our Work (highlighted_work field, grid + carousel mobile)
          ================================================================ */}
      {workLoaded && highlightedWorkItems.length > 0 && (
        <PageSection className="home-view__work-section">
          <ContentWrapper>
            <ContentWide
              alignment="center"
              className="home-view__section-header"
            >
              <h1 className="home-view__section-title">
                {intl.formatMessage(messages.ourWorkTitle)}
              </h1>
              <p>
                Learn about the projects, services, and expertise behind our
                impact.
              </p>
            </ContentWide>
            <ContentWide alignment="center">
              <ResponsiveComponentSwitch
                breakpoints={[
                  {
                    minWidth: 768,
                    component: (
                      <ResponsiveGrid breakpoints={WORK_GRID_BREAKPOINTS}>
                        {workCards}
                      </ResponsiveGrid>
                    ),
                  },
                ]}
                defaultComponent={<Carousel>{workCards}</Carousel>}
              />
              <div className="home-view__cta-link">
                <ESSButton variant="link-icon" href="/our-work">
                  {intl.formatMessage(messages.exploreMore)}
                  <FontAwesomeIcon icon={faChevronRight} size="sm" />
                </ESSButton>
              </div>
            </ContentWide>
          </ContentWrapper>
        </PageSection>
      )}

      {/* ================================================================
          SECTION 5: Latest News (3 most recent news items)
          ================================================================ */}
      <PageSection
        className="home-view__news-section"
        backgroundColor="var(--color-secondary-02)"
      >
        <ContentWrapper>
          <ContentWide alignment="center" className="home-view__section-header">
            <h1 className="home-view__section-title">
              {intl.formatMessage(messages.newsTitle)}
            </h1>
            <p>
              Stay up to date with our latest news, achievements, and
              activities.
            </p>
          </ContentWide>
          <ContentWide alignment="center">
            {newsLoaded && newsCards.length > 0 && (
              <ResponsiveComponentSwitch
                breakpoints={[
                  {
                    minWidth: 1024,
                    component: (
                      <ResponsiveGrid breakpoints={NEWS_GRID_BREAKPOINTS}>
                        {newsCards}
                      </ResponsiveGrid>
                    ),
                  },
                ]}
                defaultComponent={<Carousel>{newsCards}</Carousel>}
              />
            )}
            <div className="home-view__cta-link">
              <ESSButton variant="link-icon" href="/news">
                {intl.formatMessage(messages.exploreMoreNews)}
                <FontAwesomeIcon icon={faChevronRight} size="sm" />
              </ESSButton>
            </div>
          </ContentWide>
        </ContentWrapper>
      </PageSection>

      {/* ================================================================
          SECTION 6: Partners (images from partners folder inside home content)
          ================================================================ */}
      {partnersLoaded && partnerCards.length > 0 && (
        <PageSection className="home-view__partners-section">
          <ContentWrapper>
            <ContentNarrow alignment="center">
              <RichText
                className="home-view__partners-text"
                content={intl.formatMessage(messages.partnersText)}
              />
              <div className="home-view__partners-logos">{partnerCards}</div>
            </ContentNarrow>
          </ContentWrapper>
        </PageSection>
      )}
    </div>
  );
};

export default HomeView;
